import { Fragment, ReactNode } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface ErrorPopoverProps {
  trigger: ReactNode;
  title?: string;
  message: string;
  code?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export default function ErrorPopover({ 
  trigger, 
  title = '错误', 
  message, 
  code,
  position = 'top'
}: ErrorPopoverProps) {
  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = () => {
    switch (position) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2 -mt-1';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2 -mb-1';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2 -mr-1';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2 -ml-1';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2 -mt-1';
    }
  };

  return (
    <Popover className="relative">
      <Popover.Button as={Fragment}>
        {trigger}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className={`absolute z-50 w-80 ${getPositionClasses()}`}>
          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="relative bg-white dark:bg-gray-800 p-4">
              {/* Arrow */}
              <div className={`absolute ${getArrowClasses()}`}>
                <div className="border-8 border-transparent border-t-white dark:border-t-gray-800"></div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {title}
                  </h3>
                  <div className="mt-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {message}
                    </p>
                    {code && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        错误代码: {code}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}